import {
  isSectionComponentLoaded,
  loadSectionComponents,
  sectionRegistry,
} from '../canvas/sectionRegistry';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  DEFAULT_THEME_SETTINGS,
  PageRenderer,
  instantiateSection,
  type PageDocument,
} from '@aquilixshop/ui-react';
import { useBuilderStore } from '../state/builderStore';
import { sectionTypeIcon } from '../ui/icons';

/**
 * SectionPreview — a LIVE, scaled-down thumbnail of a section as it will look once added (founder request
 * 2026-07-05, Shopify-parity: "preview the component before adding it"). Rendered inside the Add-section
 * picker's preview pane, keyed on the hovered/focused section type.
 *
 * It is the SAME render path the storefront + the builder's clean-Preview use — no second engine, no
 * screenshot, no per-section maintained thumbnail. We instantiate the section's DEFAULT preset (exactly
 * what `insertSection` would add) into a one-section document and paint it through `PageRenderer` with the
 * store's live edit `RenderContext` (data-driven sections fall back to demo content, so featured-product /
 * collection / cart previews look populated instead of blank — see _shared/product.ts).
 * That context is copied with `demoMedia: false`, so NO demo photography reaches the pane: every media slot
 * draws the neutral scenic placeholder, which is exactly what the seller gets once the section is inserted.
 * It is ALSO copied with the DEFAULT palette: le pane est une planche de référence, pas un aperçu de la
 * boutique — sur un thème à fond noir, chaque vignette virait à la bande noire (voir `neutralTheme`).
 *
 * Faithfulness by construction: the section is laid out at a fixed desktop {@link DESIGN_WIDTH} (so
 * responsive `@container aqx-page` sections resolve their desktop layout) and CSS-scaled to fit the pane.
 * `PageRenderer` already wraps every section in a `SectionErrorBoundary`, so a throwing section degrades to
 * a labelled placeholder rather than crashing the picker; if the type is unknown or no context exists yet,
 * we fall back to the section's glyph — the pane is never blank.
 *
 * THE ONE ASYNC STEP. This is the only place in the editor that paints a section the open page does not
 * contain, so it is the only place whose component may not be loaded yet (the load path fetches a page's
 * sections before it paints them — see `canvas/sectionRegistry.ts`). The pane shows the section's glyph
 * while the chunk is in flight, which is the same fallback it already had, and swaps to the live preview
 * when it lands. Without this the picker would paint an empty pane for every section not on the page —
 * which, in a picker, is most of them.
 *
 * Non-interactive: the stage is `inert` + `pointer-events:none` (and its pane is `aria-hidden`), so the
 * rendered buttons/inputs/embeds can't be tabbed into or clicked — deferred vendor embeds (video/tiktok/
 * 17track) load on click only, so they never fire in the preview.
 */

/** Desktop layout width the section is measured/rendered at before scaling to the pane. */
const DESIGN_WIDTH = 1200;
/** Visible height cap for the scaled thumbnail; taller sections clip under a soft bottom fade. */
const MAX_STAGE_HEIGHT = 340;
/** Placeholder height before the first measurement, so the pane doesn't collapse then jump. */
const INITIAL_HEIGHT = 180;

export function SectionPreview({ type }: { type: string }) {
  const liveContext = useBuilderStore((s) => s.renderContext);
  const meta = useBuilderStore((s) => s.meta);
  // The STORE's language, exactly as `insertSection` passes it to instantiateSection — without it the
  // preview resolved every `@t:` marker in French while the inserted section came out in the shop's
  // language, so the thumbnail lied on every non-French store.
  const storeLanguage = useBuilderStore((s) => s.storeLanguage);
  // Les VRAIS avis de la boutique (0066), exactement comme le canvas les fusionne dans `MemoSection` — le
  // pane ne les lisait pas, alors `product-reviews` s'y prévisualisait en « 0 avis » sur une boutique qui
  // en a des centaines : le seul composant du sélecteur dont l'aperçu contredisait la page derrière lui
  // (fondatrice, 16/09/2026). Faute d'avis (page hors produit, boutique neuve), la section retombe sur sa
  // bande d'exemple — c'est `pickerPreview` ci-dessous qui l'y autorise, ici et nulle part ailleurs.
  const previewReviews = useBuilderStore((s) => s.previewReviews);

  // La PALETTE du pane est NEUTRE, toujours (direction fondatrice 2026-09-18). Le pane héritait des
  // couleurs de la boutique : fond passé au noir, chaque vignette du sélecteur devenait une bande noire où
  // l'on ne distinguait plus la forme du composant — or c'est la SEULE question que le pane répond («à quoi
  // ressemble ce composant ?»). Il rend donc sur la palette par défaut (fond blanc, texte #121212), comme la
  // planche de référence d'un catalogue. Le reste du thème RESTE celui de la boutique — polices, échelles,
  // largeur de conteneur, rayons : ce sont les COULEURS seules qui masquaient la forme.
  const neutralTheme = useMemo(
    () =>
      liveContext
        ? { ...liveContext.themeSettings, colors: DEFAULT_THEME_SETTINGS.colors }
        : undefined,
    [liveContext],
  );

  // Le pane d'aperçu ne montre JAMAIS de photo de démo (direction fondatrice 2026-09-16) : `demoMedia:
  // false` force les catalogues de démo (y compris celui du thème actif, qui embarque SES photos) à rendre
  // le placeholder scénique. Ce que la vendeuse voit ici est donc l'emplacement vide qu'elle remplira, et
  // non une image qui disparaît à l'insertion. Le canvas, lui, garde le catalogue du thème intact.
  const context = useMemo(
    () =>
      liveContext && neutralTheme
        ? {
            ...liveContext,
            themeSettings: neutralTheme,
            demoMedia: false,
            pickerPreview: true,
            ...(previewReviews ? { reviews: previewReviews } : {}),
          }
        : liveContext,
    [liveContext, neutralTheme, previewReviews],
  );

  const frameRef = useRef<HTMLDivElement>(null); // visible clipped box → measures pane width
  const stageRef = useRef<HTMLDivElement>(null); // full-width unscaled render → measures natural height

  const [paneWidth, setPaneWidth] = useState(0);
  const [naturalHeight, setNaturalHeight] = useState(0);

  // A one-section document from the section's DEFAULT preset — byte-for-byte what `insertSection` adds.
  // Memoized per (type, meta) so the heavy section tree isn't rebuilt on every resize measurement tick.
  // Re-render once this type's render code arrives. `isSectionComponentLoaded` is read into state rather
  // than called during render, because a component that loads must MOVE the pane off its glyph, and only a
  // state change does that.
  const [componentReady, setComponentReady] = useState(() => isSectionComponentLoaded(type));
  useEffect(() => {
    if (isSectionComponentLoaded(type)) {
      setComponentReady(true);
      return;
    }
    setComponentReady(false);
    let live = true;
    void loadSectionComponents([type]).then(() => {
      // `isSectionComponentLoaded`, not an unconditional true: a chunk that failed leaves the pane on its
      // glyph instead of swapping to an empty preview.
      if (live) setComponentReady(isSectionComponentLoaded(type));
    });
    return () => {
      live = false;
    };
  }, [type]);

  const doc = useMemo<PageDocument | null>(() => {
    const def = sectionRegistry[type];
    if (!def) return null;
    try {
      return {
        ...meta,
        sections: [instantiateSection(def, 0, storeLanguage || undefined)],
      } as PageDocument;
    } catch {
      return null;
    }
  }, [type, meta, storeLanguage]);

  // Measure the visible frame width and the stage's natural (unscaled) height. A single ResizeObserver on
  // both re-measures as the pane resizes AND as late assets (demo images / icon font) reflow the section.
  useLayoutEffect(() => {
    const frame = frameRef.current;
    const stage = stageRef.current;
    if (!frame || !stage) return;
    // Belt-and-suspenders with CSS pointer-events:none — remove the rendered controls from tab order too.
    stage.setAttribute('inert', '');
    const measure = () => {
      setPaneWidth(frame.clientWidth);
      setNaturalHeight(stage.offsetHeight); // offsetHeight is the layout box at DESIGN_WIDTH, ignores scale
    };
    const ro = new ResizeObserver(measure);
    ro.observe(frame);
    ro.observe(stage);
    measure();
    return () => ro.disconnect();
  }, [doc]);

  // No context yet, unknown/dead section, or render code still in flight → the section's glyph (never a
  // blank pane).
  if (!context || !doc || !componentReady) {
    return (
      <span className="bld-addmenu__preview-icon">
        {sectionTypeIcon(sectionRegistry[type]?.meta.icon, 28)}
      </span>
    );
  }

  const scale = paneWidth > 0 ? paneWidth / DESIGN_WIDTH : 0;
  const scaledHeight = naturalHeight * scale;
  const clipped = scaledHeight > MAX_STAGE_HEIGHT + 1;
  const frameHeight = scale > 0 ? Math.min(scaledHeight, MAX_STAGE_HEIGHT) : INITIAL_HEIGHT;

  return (
    <div
      ref={frameRef}
      className={clipped ? 'bld-section-preview is-clipped' : 'bld-section-preview'}
      style={{ height: `${Math.round(frameHeight)}px` }}
    >
      <div
        ref={stageRef}
        className="bld-section-preview__stage"
        style={{
          width: DESIGN_WIDTH,
          transform: `scale(${scale || 0.0001})`,
          // Hide the unscaled full-width flash before the first measurement resolves the scale.
          visibility: scale > 0 ? 'visible' : 'hidden',
        }}
      >
        <PageRenderer document={doc} context={context} />
      </div>
    </div>
  );
}
